import React from 'react';
import { Box, Button, IconButton, Menu, MenuItem, Typography, Tooltip } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ViewListIcon from '@mui/icons-material/ViewList';
import StatusFilterTabs from '../../Components/Tab/tab.tsx';
import Dropdown from '../../Components/DropDown/drop-down.tsx';
import Newpagination from '../../Components/Pagination/pagination.tsx';
import TripMap from './trip-map.tsx';
import mapsIcon from '../../../assets/map.png';

interface TripsDesktopViewProps {
    orderRef: React.MutableRefObject<any>;
    currentTabs: string[];
    activeMobileFilter: string;
    handleTabChange: (val: string) => void;
    statusMenuAnchor: HTMLElement | null;
    setStatusMenuAnchor: (el: HTMLElement | null) => void;
    pageSize: number;
    updateField: (e: any) => void;
    showEntries: any[];
    searchTerm: string;
    handleInputChange: (e: any) => void;
    handleSearchClick: () => void;
    companiesId: string | number;
    userRole: string;
    handleOpenAddVehicle: () => void;
    isClientCompanyAdmin: boolean;
    handleStartTrip: () => void;
    showMap: boolean;
    setShowMap: (val: boolean) => void;
    exportToExcel: () => void;
    rows: any[];
    columns: GridColDef[];
    totalRecords: number;
    filteredTrips: any[];
    activeTotalRecords: number;
    page: number;
    handlePageClick: (e: any, page: number) => void;
    tripOptions: string[];
    dynamicStatus: string;
    handleStatusSelect: (status: string) => void;
    mapRef: React.MutableRefObject<google.maps.Map | null>;
}

const TripsDesktopView: React.FC<TripsDesktopViewProps> = ({
    orderRef, currentTabs, activeMobileFilter, handleTabChange, statusMenuAnchor, setStatusMenuAnchor,
    pageSize, updateField, showEntries, searchTerm, handleInputChange, handleSearchClick,
    companiesId, userRole, handleOpenAddVehicle, isClientCompanyAdmin, handleStartTrip,
    showMap, setShowMap, exportToExcel, rows, columns, totalRecords, filteredTrips,
    activeTotalRecords, page, handlePageClick, tripOptions, dynamicStatus, handleStatusSelect, mapRef
}) => {

    return (
        <div ref={orderRef}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2, marginLeft: { xs: '10px', sm: '185px' }, marginRight: '40px', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <StatusFilterTabs tabs={currentTabs} activeTab={activeMobileFilter} onTabChange={handleTabChange} />
                    <IconButton onClick={(e) => setStatusMenuAnchor(e.currentTarget)} size="small" sx={{ marginLeft: '5px', bgcolor: '#f5f5f5', border: '1px solid #ddd', '&:hover': { bgcolor: '#e0e0e0' } }}>
                        <ArrowDropDownIcon />
                    </IconButton>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Typography sx={{ whiteSpace: 'nowrap', fontSize: '14px', color: '#666' }}>Show:</Typography>
                    <Dropdown options={showEntries} selectedValue={pageSize.toString()} handleChange={updateField} width='80px' height='32px' />
                </Box>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#fff', borderRadius: '25px', border: '1px solid #d1d1d1', px: 2, width: '300px', height: '38px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', transition: 'border-color 0.2s', '&:focus-within': { borderColor: '#000000ff' } }}>
                        <input placeholder="Search vehicles" value={searchTerm} onChange={handleInputChange} onKeyDown={(e) => e.key === 'Enter' && handleSearchClick()} style={{ border: 'none', outline: 'none', width: '100%', fontSize: '0.85rem', color: '#333' }} />
                        <IconButton size="small" onClick={handleSearchClick} sx={{ ml: 1 }}>
                            <SearchIcon sx={{ fontSize: '20px', color: '#ed1b1b' }} />
                        </IconButton>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight:{sm:'-20px'} }}>
                    {(companiesId == 1 || userRole === 'platform_user') && (
                        <button onClick={handleOpenAddVehicle} className="custom-button" style={{ background: 'linear-gradient(1turn, rgb(211, 211, 211), rgb(255, 255, 255))', color: 'black', border: '1px solid #ccc', fontWeight: 'bold', height: '35px', borderRadius: '4px', padding: '0 15px', cursor: 'pointer' }}>
                            Add Vehicle
                        </button>
                    )}
                    
                    {!isClientCompanyAdmin && (
                        <Button variant="contained" sx={{ backgroundColor: 'green', color: 'white', borderRadius: '20px', textTransform: 'none', height: '32px', fontWeight: 'bold', '&:hover': { backgroundColor: '#1b5e20' } }} size="small" onClick={() => handleStartTrip()}>
                            Start Trip
                        </Button>
                    )}

                    <Tooltip title={showMap ? "Close Map View" : "Open Map View"}>
                        <Button variant={showMap ? "contained" : "outlined"} onClick={() => setShowMap(!showMap)} sx={{ minWidth: '45px', height: '32px', borderRadius: '20px',  border:'none' }}>
                            {showMap ? <ViewListIcon fontSize="small" /> : <img src={mapsIcon} alt="Map View" style={{ width: '30px', height: '30px' }} />}
                        </Button>
                    </Tooltip>

                    <Button className='custom-grey-button' sx={{ height: '32px', minWidth: '45px' }} onClick={exportToExcel}>
                        <DownloadIcon fontSize="small" />
                    </Button>
                </Box>
            </Box>

            <Box className="table-container custom-data-grid"  >
                <DataGrid
                    key={activeMobileFilter}
                    rows={rows}
                    columns={columns}
                    sx={{ flex: 1, minWidth: 0, '& .MuiDataGrid-cell:focus': { outline: 'none' } }}
                    rowCount={activeMobileFilter === 'All' ? totalRecords : filteredTrips.length}
                    getRowHeight={() => 'auto'}
                    autoHeight={false}
                    disableRowSelectionOnClick
                    disableColumnResize
                    hideFooterPagination
                    isCellEditable={(params) => {
                        const status = params.row.status || '';
                        const isAbsent = status === 'Absent';
                        if ( isAbsent) return false;
                        return true;
                    }}
                    classes={{ columnHeader: 'red-header' }}
                />

                {showMap && (
                    <Box sx={{ flex: '0 0 40%', height: '100%', borderLeft: '1px solid #ccc' }}>
                        <TripMap trips={filteredTrips} isMobile={false} mapRef={mapRef} />
                    </Box>
                )}
            </Box>

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px', marginLeft: '180px' }}>
                <Newpagination rows={activeTotalRecords} pageSize={pageSize} page={page} handlePageChange={handlePageClick} />
            </div>

            <Menu anchorEl={statusMenuAnchor} open={Boolean(statusMenuAnchor)} onClose={() => setStatusMenuAnchor(null)}>
                {tripOptions.map((status) => (
                    <MenuItem key={status} selected={dynamicStatus === status} onClick={() => handleStatusSelect(status)}>
                        {status}
                    </MenuItem>
                ))}
            </Menu>
        </div>
    );
};

export default TripsDesktopView;